import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";

import {
  Typography,
  IconButton,
  Button,
  Box,
  Paper,
  Collapse,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";

import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import AppHeader from "./AppHeader";

import { useNavigate } from "react-router-dom";

import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);


const API_URL = process.env.REACT_APP_ATTENDANCE_API_URL || "http://localhost:3000/api/attendance";
const API_URL1 = process.env.REACT_APP_API_URL1;

const getTenantHeader = () => ({
  "X-Tenant": localStorage.getItem("tenant") || "",
});


const getBearerToken = () => {
  // Web admin may or may not store a token; keep optional.
  return (
    localStorage.getItem("userToken") ||
    localStorage.getItem("token") ||
    ""
  );
};

const AttendanceList = ({ setIsLoggedIn }) => {
  console.log('🔄 Component rendered');
  
  const gridRef = useRef(null);

  const [filtersOpen, setFiltersOpen] = useState(true);
  // Helper: Today's date in YYYY-MM-DD (Yangon)
  const todayStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Yangon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  const [filters, setFilters] = useState({
    date_from: todayStr,
    date_to: todayStr,
    staff_code: "",
    staff_name: "",
    department: "",
    status: "",
  });

  // Separate state for applied filters (the ones sent to backend)
  const [appliedFilters, setAppliedFilters] = useState({
    date_from: todayStr,
    date_to: todayStr,
    staff_code: "",
    staff_name: "",
    department: "",
    status: "",
  });

  const [rowData, setRowData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");

  // Photo preview (signed URL)
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoTitle, setPhotoTitle] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const handleClosePhotoDialog = () => {
    setPhotoDialogOpen(false);

    if (photoUrl && photoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(photoUrl);
    }

    setPhotoUrl("");
    setPhotoError("");
  };

  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize);

  const [page, setPage] = useState(1);
  
  
  const navigate = useNavigate();

  console.log('📊 Current State:', {
    filters,
    appliedFilters,
    page,
    rowDataLength: rowData.length
  });

  
  // Convert DB time → Local Myanmar Time for datetime input
  const convertToLocalInputFormat = (val) => {
    if (!val) return "";
    const d = new Date(val);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };


  const handleExportExcel = async () => {
    try {
      // Export should be based on applied filters (server-side export),
      // not on the current page's rowData.
      if (!appliedFilters?.date_from || !appliedFilters?.date_to) {
        alert("Please select From Date and To Date first.");
        return;
      }

      const tenantHeaders = getTenantHeader();
      const token = getBearerToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Build query params from applied filters (same as list)
      const params = {
        ...appliedFilters,
        // Server export should ignore pagination, but we won't send page/pageSize anyway.
      };

      // IMPORTANT: This endpoint must return an XLSX file (Content-Type:
      // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet)
      const exportUrl = `${API_URL}/admin/export-v2`;

      const res = await axios.get(exportUrl, {
        params,
        withCredentials: true,
        headers: {
          ...tenantHeaders,
          ...authHeaders,
        },
        responseType: "blob",
      });

      // Try to read filename from Content-Disposition
      const disposition = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      let fileName = "";

      if (disposition && typeof disposition === "string") {
        // e.g. attachment; filename="attendance_TNL_2026-01-01-2026-01-31.xlsx"
        const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/"/g, "").trim());
        }
      }

      if (!fileName) {
        const from = appliedFilters?.date_from || "";
        const to = appliedFilters?.date_to || "";
        const tenant = (localStorage.getItem("tenant") || "").trim();
        const safeTenant = tenant ? tenant.replace(/[^a-zA-Z0-9_-]/g, "_") : "TENANT";
        fileName = `attendance_${safeTenant}_${from || "from"}-${to || "to"}.xlsx`;
      }

      // Download blob
      const blob = new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("❌ Server export failed:", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Export failed. Please try again.";
      alert(msg);
    }
  };

  const openPhotoPreview = useCallback(async (documentId, title) => {
    if (!documentId) return;

    setPhotoTitle(title || "Photo");
    // Clear previous URL and error
    setPhotoUrl(""); 
    setPhotoError("");
    setPhotoLoading(true);
    setPhotoDialogOpen(true);

    const tenantHeaders = getTenantHeader();

    try {
      // 1) Attempt to get Signed URL from Attendance Backend
      const previewRes = await axios.get(
        `${API_URL1}/admin/documents/${encodeURIComponent(documentId)}/preview`,
        {
          withCredentials: true,
          headers: { ...tenantHeaders },
        }
      );

      if (previewRes?.data?.url) {
        setPhotoUrl(previewRes.data.url);
        setPhotoLoading(false);
        return;
      }
      
      throw new Error("No URL returned from backend");
    } catch (e1) {
      console.warn("⚠️ Photo preview via backend failed; fetching binary from DMS...", e1);

      try {
        // 2) Fallback: Fetch the binary image data directly from DMS
        const token = getBearerToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        const dmsRes = await axios.get(
          `${API_URL1}/admin/documents/${encodeURIComponent(documentId)}/download`,
          {
            withCredentials: true,
            headers: {
              ...authHeaders,
              ...tenantHeaders,
            },
            // CRITICAL CHANGE: Tell Axios to expect binary data
            responseType: 'blob', 
          }
        );

        // Check if the content type is an image
        const contentType = dmsRes.headers['content-type'];
        
        if (contentType && contentType.startsWith('image')) {
          // Create a local object URL from the Blob
          const blob = new Blob([dmsRes.data], { type: contentType });
          const objectUrl = URL.createObjectURL(blob);
          
          setPhotoUrl(objectUrl);
          setPhotoLoading(false);
        } else {
          // If we got a blob but it's not an image (e.g. JSON error inside a blob)
          // We try to read it to see the error message
          const text = await dmsRes.data.text();
          let errorMessage = "Invalid content type received";
          try {
             const json = JSON.parse(text);
             if(json.message) errorMessage = json.message;
          } catch(e) { /* ignore */ }
          
          throw new Error(errorMessage);
        }

      } catch (e2) {
        console.error("❌ Photo load failed", e2);
        setPhotoError("Failed to load photo.");
        setPhotoLoading(false);
      }
    }
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "expand",
        width: 60,
        cellRenderer: params => {
          const isOffice = params.data?.work_type === "office";
          const hasMultiple = Array.isArray(params.data?.shifts) && params.data.shifts.length > 1;
          if (!isOffice || !hasMultiple) return "";
          const isOpen = expandedRows[params.data.id];
          return (
            <span
              style={{ cursor: "pointer", fontSize: "18px", color: "#1b5e20" }}
              onClick={() => {
                setExpandedRows(prev => ({
                  ...prev,
                  [params.data.id]: !prev[params.data.id]
                }));
              }}
            >
              {isOpen ? "−" : "+"}
            </span>
          );
        }
      },
      { headerName: "ID", field: "id", width: 60 },
      { headerName: "Staff Code", field: "staff_code", width: 120 },
      { headerName: "Name", field: "full_name", width: 150 },
      { headerName: "Dept", field: "department", width: 120 },
      { headerName: "Type", field: "work_type", width: 100 },
      { 
        headerName: "Shift Hrs", 
        field: "total_shift_hours", 
        width: 110,
        cellStyle: params => {
          const val = parseFloat(params.value);
          // Highlight if hours > 0 (meaning clocked out) and < 3 (potential cheat/error)
          if (!isNaN(val) && val > 0 && val < 3) {
            return { backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 'bold' };
          }
          return null;
        }
      },
      { headerName: "OT Hrs", field: "ot_hours", width: 110 },
      {
        headerName: "Date",
        field: "attendance_date",
        width: 110,
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleString("en-GB", {
            timeZone: "Asia/Yangon",
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          });
        }
      },
      {
        headerName: "Clock In",
        field: "clock_in_time",
        width: 150,
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleString("en-GB", {
            timeZone: "Asia/Yangon",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
        }
      },
      {
        headerName: "Clock Out",
        field: "clock_out_time",
        width: 150,
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleString("en-GB", {
            timeZone: "Asia/Yangon",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
        }
      },
      { headerName: "In Location", field: "clock_in_location_name", width: 150 },
      { headerName: "Out Location", field: "clock_out_location_name", width: 150 },
      { headerName: "In Beacon", field: "clock_in_beacon_name", width: 150 },
      { headerName: "Out Beacon", field: "clock_out_beacon_name", width: 150 },
      { headerName: "Method", field: "clock_in_method", width: 120 },
      {
        headerName: "In Photo",
        field: "clock_in_document_id",
        width: 110,
        cellRenderer: (params) => {
          const docId = params.data?.clock_in_document_id;
          if (!docId) return "—";
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openPhotoPreview(docId, "Clock-In Photo")}
            >
              📷 View
            </Button>
          );
        },
      },
      {
        headerName: "Out Photo",
        field: "clock_out_document_id",
        width: 110,
        cellRenderer: (params) => {
          const docId = params.data?.clock_out_document_id;
          if (!docId) return "—";
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openPhotoPreview(docId, "Clock-Out Photo")}
            >
              📷 View
            </Button>
          );
        },
      },
      { 
        headerName: "Status", 
        field: "status", 
        width: 130,
        cellRenderer: (params) => {
          const val = parseFloat(params.data?.total_shift_hours);
          const isAbnormal = !isNaN(val) && val > 0 && val < 3;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {params.value}
              </Typography>
              {isAbnormal && (
                <Box
                  sx={{
                    bgcolor: "#ffcdd2",
                    color: "#b71c1c",
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: "0.65rem",
                    fontWeight: "bold",
                    lineHeight: 1,
                    border: "1px solid #ef9a9a",
                    whiteSpace: 'nowrap'
                  }}
                >
                  ABNORMAL
                </Box>
              )}
            </Box>
          );
        }
      },
      { headerName: "Notes", field: "notes", width: 200 },
      {
        headerName: "Edit",
        field: "edit",
        width: 100,
        cellRenderer: (params) => {
          return (
            <Button variant="outlined" size="small" onClick={() => {
              const r = params.data;
              // Guard: prevent editing office record with multiple shifts
              if (r.work_type === "office" && Array.isArray(r.shifts) && r.shifts.length > 1) {
                alert("Multiple shifts - expand and edit a specific shift.");
                return;
              }
              setEditRecord(r);
              setEditClockIn(convertToLocalInputFormat(r.clock_in_time));
              setEditClockOut(convertToLocalInputFormat(r.clock_out_time));
              setEditDialogOpen(true);
            }}>
              Edit
            </Button>
          );
        }
      }
    ],
    [expandedRows, openPhotoPreview]
  );

  const DetailRenderer = ({ data }) => {
    if (data?.work_type !== "office" || !data?.shifts || data.shifts.length <= 1) return null;
    return (
      <div style={{
        marginLeft: "40px",
        padding: "10px",
        background: "#f9fbe7",
        borderLeft: "3px solid #8bc34a",
        marginBottom: "10px"
      }}>
        <strong>Shift Details</strong>
        {data.shifts.map((s, idx) => (
          <div key={idx} style={{ marginTop: "6px", padding: "4px 0", borderBottom: "1px solid #ddd" }}>
            <div>Shift ID: {s.id}</div>
            <div>Clock In: {s.clock_in_time ? new Date(s.clock_in_time).toLocaleString("en-GB", {
              timeZone: "Asia/Yangon",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false
            }) : ""}</div>
            <div>Clock Out: {s.clock_out_time ? new Date(s.clock_out_time).toLocaleString("en-GB", {
              timeZone: "Asia/Yangon",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false
            }) : ""}</div>
            <div style={{ color: (parseFloat(s.hours) > 0 && parseFloat(s.hours) < 3) ? "#d32f2f" : "inherit", fontWeight: (parseFloat(s.hours) > 0 && parseFloat(s.hours) < 3) ? "bold" : "normal" }}>
              Hours: {s.hours} {(parseFloat(s.hours) > 0 && parseFloat(s.hours) < 3) && <span style={{ fontSize: '0.7rem', marginLeft: '8px', padding: '2px 6px', border: '1px solid #d32f2f', borderRadius: '4px', backgroundColor: '#ffebee' }}>ABNORMAL</span>}
            </div>
            <div>Method: {s.method}</div>
            <div>In Location: {s.clock_in_location_name}</div>
            <div>Out Location: {s.clock_out_location_name}</div>
            <div>In Beacon: {s.clock_in_beacon_name}</div>
            <div>Out Beacon: {s.clock_out_beacon_name}</div>
            <div>
              In Photo:{" "}
              {s.clock_in_document_id ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openPhotoPreview(s.clock_in_document_id, `Clock-In Photo (Shift ${s.id})`)}
                  sx={{ ml: 1 }}
                >
                  📷 View
                </Button>
              ) : (
                "—"
              )}
            </div>
            <div>
              Out Photo:{" "}
              {s.clock_out_document_id ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openPhotoPreview(s.clock_out_document_id, `Clock-Out Photo (Shift ${s.id})`)}
                  sx={{ ml: 1 }}
                >
                  📷 View
                </Button>
              ) : (
                "—"
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  // Use useCallback to memoize the fetch function
  const fetchData = useCallback(async (currentPage, currentFilters) => {
    console.log('🚀 fetchData CALLED');
    console.log('🔍 Fetching with filters:', currentFilters);
    console.log('📄 Page:', currentPage);
    
    try {
      const url = `${API_URL}/admin/list-v2`;
      const params = {
        page: currentPage,
        pageSize,
        ...currentFilters,
      };
      
      console.log('🌐 API URL:', url);
      console.log('📦 Request params:', params);
      
      const res = await axios.get(url, {
        params,
        withCredentials: true,
        headers: {
          "X-Tenant": localStorage.getItem("tenant") || "",
        },
      });

      console.log('✅ Response received:', res.data);

      if (res.data?.success) {
        const mapped = res.data.rows.map((r) => {
          const mappedItem = {
            id: r.record_id,
            staff_id: r.staff_id,
            staff_code: r.staff_code,
            full_name: r.full_name,
            department: r.department,
            work_type: r.work_type,
            attendance_date: r.attendance_date,
            clock_in_time: r.clock_in_time,
            clock_out_time: r.clock_out_time,
            clock_in_location_name: r.clock_in_location_name,
            clock_out_location_name: r.clock_out_location_name,
            clock_in_beacon_name: r.clock_in_beacon_name,
            clock_out_beacon_name: r.clock_out_beacon_name,
            clock_in_method: r.clock_in_method,
            total_shift_hours: r.total_shift_hours,
            ot_hours: r.ot_hours,
            status: r.status,
            notes: r.notes,
            clock_in_document_id: r.clock_in_document_id || null,
            clock_out_document_id: r.clock_out_document_id || null,
            shifts: r.shifts,
          };
          if (r.work_type === "office" && Array.isArray(r.shifts) && r.shifts.length === 1) {
            mappedItem.clock_in_beacon_name = r.shifts[0].clock_in_beacon_name;
            mappedItem.clock_out_beacon_name = r.shifts[0].clock_out_beacon_name;
            mappedItem.clock_in_location_name = r.shifts[0].clock_in_location_name;
            mappedItem.clock_out_location_name = r.shifts[0].clock_out_location_name;
            mappedItem.clock_in_method = r.shifts[0].method || r.shifts[0].clock_in_method || mappedItem.clock_in_method;
            mappedItem.clock_in_document_id = r.shifts[0].clock_in_document_id || mappedItem.clock_in_document_id;
            mappedItem.clock_out_document_id = r.shifts[0].clock_out_document_id || mappedItem.clock_out_document_id;
          }
          return mappedItem;
        });

        console.log('📝 Mapped data:', mapped);
        setRowData(mapped);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("❌ Error fetching attendance:", err);
      console.error("Error details:", err.response?.data);
    }
  }, [pageSize]);

  // Fetch data when page or appliedFilters change
  useEffect(() => {
    console.log('⚡ useEffect TRIGGERED');
    console.log('⚡ Dependencies:', { page, appliedFilters });

    // Load automatically if we have a date range (starts with today by default)
    if (!appliedFilters.date_from || !appliedFilters.date_to) {
      console.log('⏳ Skipping fetch — waiting for date range');
      setRowData([]); // ensure empty grid
      return;
    }

    fetchData(page, appliedFilters);
  }, [page, appliedFilters, fetchData]);

  // Handler for Apply button - updates appliedFilters which triggers useEffect
  const handleApplyFilters = () => {
    console.log('🎯 handleApplyFilters CLICKED');
    console.log('🎯 Current filters:', filters);
    console.log('🎯 Setting appliedFilters to:', filters);
    
    setAppliedFilters({ ...filters }); // Create new object reference
    setPage(1);
    
    console.log('🎯 State update dispatched');
  };

  const handleResetFilters = () => {
    console.log('🔄 handleResetFilters CLICKED');
    
    const resetFilters = {
      date_from: todayStr,
      date_to: todayStr,
      staff_code: "",
      staff_name: "",
      department: "",
      status: "",
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPage(1);
  };

 // Handler for manual refresh button
const handleRefresh = () => {
  console.log('🔃 handleRefresh CLICKED — now same as Reset');
  handleResetFilters();
};

  const handleLogout = () => {
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    navigate("/login");
  };

// Convert datetime-local → MySQL local time string
const toMySQL = (value) => {
  if (!value) return null;
  return value.replace("T", " ") + ":00";
};

const saveAttendanceChanges = async () => {
  try {
    if (!editRecord) return;

    const updates = {};

    if (editClockIn) {
      updates.clock_in_time = toMySQL(editClockIn);
    }
    if (editClockOut) {
      updates.clock_out_time = toMySQL(editClockOut);
    }

    if (Object.keys(updates).length === 0) {
      alert("No changes to save.");
      return;
    }

    // Derive correct attendanceId before posting
    let attendanceId = editRecord.id;
    if (editRecord.work_type === "office") {
      if (Array.isArray(editRecord.shifts) && editRecord.shifts.length === 1) {
        attendanceId = editRecord.shifts[0].id;
      } else {
        alert("Multiple shifts detected. Expand and select a shift to edit.");
        return;
      }
    }

    await axios.post(
      `${API_URL}/admin/update-core`,
      {
        record_id: attendanceId,
        updates
      },
      {
        withCredentials: true,
        headers: {
          "X-Tenant": localStorage.getItem("tenant") || "",
        },
      }
    );

    setEditDialogOpen(false);
    fetchData(page, appliedFilters);

  } catch (err) {
    console.error("❌ Save failed:", err);
    alert("Save failed. Please try again.");
  }
};

  return (
   <> 
    <Box sx={{ minHeight: "100vh", bgcolor: "#e8f5e9" }}>
     <AppHeader onLogout={handleLogout} />

      <Box sx={{ maxWidth: "1600px", mx: "auto", mt: 3, px: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" sx={{ color: "#1b5e20", fontWeight: 600 }}>
            Attendance Records
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                console.log('👁️ Toggle filters clicked');
                setFiltersOpen((prev) => !prev);
              }}
            >
              {filtersOpen ? "Hide Filters" : "Show Filters"}
            </Button>

            <Button
              variant="contained"
              onClick={handleExportExcel}
              disabled={!appliedFilters.date_from || !appliedFilters.date_to}
            >
              Export Excel
            </Button>

            <IconButton
              onClick={handleRefresh}
              disabled={!appliedFilters.date_from || !appliedFilters.date_to}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>

        <Collapse in={filtersOpen}>
          <Paper sx={{ p: 2, mb: 2, bgcolor: "#f1f8e9", borderRadius: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <TextField
                label="From Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.date_from}
                sx={{ minWidth: 140 }}
                onChange={(e) => {
                  console.log('📅 From Date changed:', e.target.value);
                  setFilters((f) => ({ ...f, date_from: e.target.value }));
                }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.date_to}
                sx={{ minWidth: 140 }}
                onChange={(e) => {
                  console.log('📅 To Date changed:', e.target.value);
                  setFilters((f) => ({ ...f, date_to: e.target.value }));
                }}
              />
              <TextField
                label="Staff Code"
                size="small"
                value={filters.staff_code}
                sx={{ minWidth: 120 }}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, staff_code: e.target.value }))
                }
              />
              <TextField
                label="Staff Name"
                size="small"
                value={filters.staff_name}
                sx={{ minWidth: 160 }}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, staff_name: e.target.value }))
                }
              />
              <TextField
                label="Department"
                size="small"
                value={filters.department}
                sx={{ minWidth: 140 }}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, department: e.target.value }))
                }
              />

              <TextField
                select
                label="Status"
                size="small"
                value={filters.status}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, status: e.target.value }))
                }
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="present">Present</MenuItem>
                <MenuItem value="absent">Absent</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
              </TextField>

             <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: "100%" }}>
               <Button variant="outlined" onClick={handleResetFilters}>
                 Reset
               </Button>
               <Button 
                 variant="contained" 
                 onClick={() => {
                   console.log('🔘 Apply button DOM clicked');
                   handleApplyFilters();
                 }}
               >
                 Apply
               </Button>
             </Stack>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
             
            </Stack>
          </Paper>
        </Collapse>

        <Paper sx={{ p: 1 }}>
          <div
            className="ag-theme-alpine"
            style={{ height: 600, width: "100%",overflow: "visible" }}
          >
            <AgGridReact
              popupParent={document.body}
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowStyle={(params) => {
                const note = params.data?.notes?.toLowerCase() || "";
                if (note.includes("system-clockout")) {
                  return { backgroundColor: "#ffcccc" };
                }
                if (note.includes("manual_entry") || note.includes("manual_adjustment")) {
                  return { backgroundColor: "#e0e0e0" };
                }
                return null;
              }}
            />
          </div>
          {rowData.map(row =>
            expandedRows[row.id] ? (
              <DetailRenderer key={`detail-${row.id}`} data={row} />
            ) : null
          )}
          <Typography variant="body2" sx={{ mt: 1, textAlign: "right", color: "#555" }}>
            Total Rows: {total}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
              <TextField
                select
                size="small"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1); // Reset to first page when page size changes
                }}
                sx={{ width: 80 }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button 
                variant="outlined" 
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </Button>

              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Page {page} of {totalPages || 1}
              </Typography>

              <Button 
                variant="outlined"
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage(prev => prev + 1)}
              >
                Next
              </Button>
            </Stack>

            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Total: {total} records
            </Typography>
          </Box>
        </Paper>
        {/* Edit Attendance Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Attendance</DialogTitle>
          <DialogContent>
            <TextField
              label="Clock In"
              type="datetime-local"
              fullWidth
              margin="normal"
              value={editClockIn}
              onChange={(e) => setEditClockIn(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Clock Out"
              type="datetime-local"
              fullWidth
              margin="normal"
              value={editClockOut}
              onChange={(e) => setEditClockOut(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
         <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveAttendanceChanges}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Photo Preview Dialog */}
        <Dialog
          open={photoDialogOpen}
          onClose={() => {
            setPhotoDialogOpen(false);
            setPhotoUrl("");
            setPhotoError("");
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{photoTitle || "Photo"}</DialogTitle>
          <DialogContent dividers>
            {photoLoading ? (
              <Typography variant="body2">Loading photo...</Typography>
            ) : photoError ? (
              <Typography variant="body2" color="error">
                {photoError}
              </Typography>
            ) : photoUrl ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={photoUrl}
                  alt={photoTitle || "Photo"}
                  style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}
                />
              </Box>
            ) : (
              <Typography variant="body2">No photo available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClosePhotoDialog}
            >
              Close
            </Button>
            {photoUrl ? (
              <Button
                variant="outlined"
                onClick={() => window.open(photoUrl, "_blank", "noopener,noreferrer")}
              >
                Open in new tab
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
   </>
  );
};

export default AttendanceList;